import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { useTheme } from '../context/ThemeContext';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const { theme, toggleTheme } = useTheme();
  
  // Track dropdown states
  const [advertiserDropdownOpen, setAdvertiserDropdownOpen] = useState(false);
  const [publisherDropdownOpen, setPublisherDropdownOpen] = useState(false);

  const handleLogout = () => {
    authService.logout();
  };

  const toggleSidebar = () => {
    if (window.innerWidth < 992) {
      setMobileMenuOpen(!mobileMenuOpen);
    } else {
      setCollapsed(!collapsed);
    }
  };
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Simple toggle functions that don't affect other dropdowns
  const toggleAdvertiserDropdown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setAdvertiserDropdownOpen(!advertiserDropdownOpen);
  };
  
  const togglePublisherDropdown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPublisherDropdownOpen(!publisherDropdownOpen);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const sidebar = document.querySelector('.navbar-vertical');
      if (sidebar && !sidebar.contains(event.target as Node) && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  // Close mobile menu when navigating
  useEffect(() => {
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  }, [location.pathname, location.search]);

  // Check if current path is in advertiser or publisher section
  const isAdvertiserSection = location.search.includes('advertiser');
  const isPublisherSection = location.search.includes('publisher');

  // Auto-open relevant dropdown based on current section WITHOUT closing others
  useEffect(() => {
    if (isAdvertiserSection && !advertiserDropdownOpen) {
      setAdvertiserDropdownOpen(true);
    }
    if (isPublisherSection && !publisherDropdownOpen) {
      setPublisherDropdownOpen(true);
    }
  }, [isAdvertiserSection, isPublisherSection, advertiserDropdownOpen, publisherDropdownOpen]);

  return (
    <div className="page">
      {/* Navbar */}
      <header className="navbar navbar-expand-md navbar-light d-print-none">
        <div className="container-xl">
          <button className="navbar-toggler" type="button" onClick={toggleSidebar} aria-label="Toggle navigation">
            <span className="navbar-toggler-icon"></span>
          </button>
          <Link to="/dashboard" className="navbar-brand navbar-brand-autodark d-none-navbar-horizontal pe-0 pe-md-3">
            <img src="/favicon.svg" alt="BidMatch Logo" width="24" height="24" className="me-2" />
            <span className="brand-text">bidmatch.ai</span>
          </Link>
          <div className="navbar-nav flex-row order-md-last">
            {/* Theme toggle button */}
            <div className="nav-item me-2">
              <button 
                className="nav-link px-0 hide-theme-dark" 
                title="Enable dark mode" 
                onClick={toggleTheme}
                style={{ display: theme === 'light' ? 'block' : 'none' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <path d="M12 3c.132 0 .263 0 .393 0a7.5 7.5 0 0 0 7.92 12.446a9 9 0 1 1 -8.313 -12.454z" />
                </svg>
              </button>
              <button 
                className="nav-link px-0 hide-theme-light" 
                title="Enable light mode" 
                onClick={toggleTheme}
                style={{ display: theme === 'dark' ? 'block' : 'none' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <path d="M12 12m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" />
                  <path d="M3 12h1m8 -9v1m8 8h1m-9 8v1m-6.4 -15.4l.7 .7m12.1 -.7l-.7 .7m0 11.4l.7 .7m-12.1 -.7l-.7 .7" />
                </svg>
              </button>
            </div>
            
            <div className="nav-item dropdown">
              <a href="#" className="nav-link d-flex lh-1 text-reset p-0" data-bs-toggle="dropdown" aria-label="Open user menu">
                <span className="avatar avatar-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-user" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0"></path>
                    <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"></path>
                  </svg>
                </span>
              </a>
              <div className="dropdown-menu dropdown-menu-end dropdown-menu-arrow">
                <Link to="/settings" className="dropdown-item">
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-settings me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z"></path>
                    <path d="M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0"></path>
                  </svg>
                  Settings
                </Link>
                <a href="#" className="dropdown-item" onClick={handleLogout}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-logout me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M14 8v-2a2 2 0 0 0 -2 -2h-7a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h7a2 2 0 0 0 2 -2v-2"></path>
                    <path d="M9 12h12l-3 -3"></path>
                    <path d="M18 15l3 -3"></path>
                  </svg>
                  Logout
                </a>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="page-wrapper">
        {/* Mobile sidebar overlay */}
        {mobileMenuOpen && (
          <div className="mobile-overlay show" onClick={toggleMobileMenu}></div>
        )}

        {/* Sidebar */}
        <aside 
          className={`navbar navbar-vertical navbar-expand-lg ${collapsed ? 'navbar-collapsed' : ''} ${mobileMenuOpen ? 'show-mobile' : ''}`}
          style={{ 
            maxWidth: mobileMenuOpen && window.innerWidth < 992 ? '50%' : undefined,
            width: mobileMenuOpen && window.innerWidth < 992 ? '50%' : undefined
          }}
        >
          <div className="container-fluid">
            {/* Mobile header with close button */}
            <div className="d-flex d-lg-none align-items-center justify-content-between mb-0" style={{ width: '100%' }}>
              <Link to="/dashboard" className="navbar-brand">
                <img src="/favicon.svg" alt="BidMatch Logo" width="20" height="20" className="me-2" />
                <span className="brand-text">bidmatch.ai</span>
              </Link>
              <button className="btn-close text-reset" onClick={toggleMobileMenu} style={{ marginLeft: 'auto' }}></button>
            </div>
            
            {/* Navigation items */}
            <ul className="navbar-nav pt-0">
              {/* Advertiser section */}
              <li className={`nav-item dropdown ${isAdvertiserSection ? 'active' : ''}`}>
                <a className={`nav-link dropdown-toggle ${advertiserDropdownOpen ? 'show' : ''}`} 
                   href="#navbar-advertiser" 
                   onClick={toggleAdvertiserDropdown}>
                  <span className="nav-link-icon d-md-none d-lg-inline-block">
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-ad" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                      <path d="M3 5m0 2a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z"></path>
                      <path d="M7 15v-4a2 2 0 0 1 4 0v4"></path>
                      <path d="M7 13h4"></path>
                      <path d="M17 9v6h-1.5a1.5 1.5 0 1 1 1.5 -1.5"></path>
                    </svg>
                  </span>
                  <span className="nav-link-title">Advertiser</span>
                </a>
                <div className={`dropdown-menu ${advertiserDropdownOpen ? 'show' : ''}`}>
                  {/* Dropdown content remains the same */}
                <Link to="/dashboard?section=advertiser-campaigns" 
                        className={`dropdown-item ${searchParams.get('section') === 'advertiser-campaigns' ? 'active' : ''}`}
                        onClick={() => setMobileMenuOpen(false)}>
                    Campaigns
                  </Link>
                  <Link to="/dashboard?section=advertiser-creatives" 
                        className={`dropdown-item ${searchParams.get('section') === 'advertiser-creatives' ? 'active' : ''}`}
                        onClick={() => setMobileMenuOpen(false)}>
                    Creatives
                  </Link>
                  <Link to="/dashboard?section=advertiser-reports" 
                        className={`dropdown-item ${searchParams.get('section') === 'advertiser-reports' ? 'active' : ''}`}
                        onClick={() => setMobileMenuOpen(false)}>
                    Reports
                  </Link>
                </div>
              </li>

              {/* Publisher section */}
              <li className={`nav-item dropdown ${isPublisherSection ? 'active' : ''}`}>
                <a className={`nav-link dropdown-toggle ${publisherDropdownOpen ? 'show' : ''}`} 
                   href="#navbar-publisher" 
                   onClick={togglePublisherDropdown}>
                  <span className="nav-link-icon d-md-none d-lg-inline-block">
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-building" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                      <path d="M3 21l18 0"></path>
                      <path d="M9 8l1 0"></path>
                      <path d="M9 12l1 0"></path>
                      <path d="M9 16l1 0"></path>
                      <path d="M14 8l1 0"></path>
                      <path d="M14 12l1 0"></path>
                      <path d="M14 16l1 0"></path>
                      <path d="M5 21v-16a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v16"></path>
                    </svg>
                  </span>
                  <span className="nav-link-title">Publisher</span>
                </a>
                <div className={`dropdown-menu publisher-menu ${publisherDropdownOpen ? 'show' : ''}`}>
                  <Link 
                    to="/dashboard?section=publisher-sites" 
                    className={`dropdown-item ${searchParams.get('section') === 'publisher-sites' ? 'active' : ''}`}
                    onClick={(e) => {
                      handleDropdownItemClick(e);
                      setMobileMenuOpen(false);
                    }}
                  >
                    Sites
                  </Link>
                  <Link 
                    to="/dashboard?section=publisher-zones" 
                    className={`dropdown-item ${searchParams.get('section') === 'publisher-zones' ? 'active' : ''}`}
                    onClick={(e) => {
                      handleDropdownItemClick(e);
                      setMobileMenuOpen(false);
                    }}
                  >
                    Zones
                  </Link>
                  <Link 
                    to="/dashboard?section=publisher-reports" 
                    className={`dropdown-item ${searchParams.get('section') === 'publisher-reports' ? 'active' : ''}`}
                    onClick={(e) => {
                      handleDropdownItemClick(e);
                      setMobileMenuOpen(false);
                    }}
                  >
                    Reports
                  </Link>
                </div>
              </li>

              {/* Finance menu item */}
              <li className="nav-item finance-item top-level-item">
                <a href="#" 
                   className={`nav-link ${searchParams.get('section') === 'balance' ? 'active' : ''}`}
                   onClick={(e) => {
                     e.preventDefault();
                     navigate('/dashboard?section=balance');
                     setMobileMenuOpen(false);
                   }}>
                  <span className="nav-link-icon d-md-none d-lg-inline-block">
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                      <path d="M17 8v-3a1 1 0 0 0 -1 -1h-10a2 2 0 0 0 0 4h12a1 1 0 0 1 1 1v3m0 4v3a1 1 0 0 1 -1 1h-12a2 2 0 0 1 -2 -2v-12"></path>
                      <path d="M20 12v4h-4a2 2 0 0 1 0 -4h4"></path>
                    </svg>
                  </span>
                  <span className="nav-link-title">Finance</span>
                </a>
              </li>

              {/* Analytics menu item */}
              <li className="nav-item analytics-item top-level-item">
                <a href="#" 
                   className={`nav-link ${searchParams.get('section') === 'analytics' ? 'active' : ''}`}
                   onClick={(e) => {
                     e.preventDefault();
                     navigate('/dashboard?section=analytics');
                     setMobileMenuOpen(false);
                   }}>
                  <span className="nav-link-icon d-md-none d-lg-inline-block">
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                      <path d="M3 12m0 1a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v6a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z"></path>
                      <path d="M9 8m0 1a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v10a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z"></path>
                      <path d="M15 4m0 1a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v14a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z"></path>
                    </svg>
                  </span>
                  <span className="nav-link-title">Analytics</span>
                </a>
              </li>
            </ul>
          </div>
        </aside>

        {/* Main content */}
        <div className="page-body">
          <div className="container-xl">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Layout;

// Add this function back since it's being used in the Publisher dropdown links
const handleDropdownItemClick = (e: React.MouseEvent) => {
  // Prevent the event from bubbling up to parent elements
  e.stopPropagation();
};
